/**
 * Колонки списку під ширину області (layout.md, розд. 3: на 1280–1535 px таблиця стоїть поруч із
 * «Сьогодні» без горизонтальної прокрутки).
 * - Ширина — за ТИПОМ поля (LST-03, layout.md, розд. 5): телефон, варіант (і поле воронки S4), суми й числа,
 *   дати, наступна активність, так / ні — фіксовані й не обрізаються; назва запису й текстові
 *   колонки гнучкі: ділять залишок порівну, не вужчі за FLEX_MIN. Код знає лише типи (ADR-0003):
 *   і чернетки S4, і реєстру платформи (`@clientline/contracts`, FLD-01).
 * - Пріоритет — з подання в метаданих (`priority`, за замовчуванням 1). Поки набір не вміщається,
 *   ховаємо найменш важливу колонку: спершу 3, потім 2, серед рівних — праву. Пріоритет 1 видно
 *   завжди; якщо не вміщаються й вони, таблиця отримує min-width і прокручується по горизонталі
 *   (600–1023 px — так і задумано).
 * Пороги залежать від набору колонок, який задають метадані, тож рахуємо їх тут, а не статичними
 * @container у CSS. Згенерувати CSS під подання не дає CSP (`style-src 'self'`).
 */
import { type RefObject, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import { flushSync } from 'react-dom';

/** Колонка вибору рядка (components.css, `.col-check`). */
export const CHECK_W = 36;
/** Найвужча гнучка колонка: 120 px тексту (рев'ю UI/UX S4, M1) + відступи комірки й запас. */
export const FLEX_MIN = 160;

/** Межі особистої ширини стовпця (LST-03; `viewPrefsBodySchema`, `viewColumnSchema`), px. */
export const COLUMN_WIDTH_MIN = 40;
export const COLUMN_WIDTH_MAX = 800;

/** Ширина стовпця в межах особистого шару. */
export const clampWidth = (px: number) =>
  Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.round(px)));

export interface ColumnSpec {
  /** Ключ поля: той самий, що в значеннях рядка. */
  key: string;
  /** Тип поля з метаданих — від нього ширина, вирівнювання й показ комірки. */
  type: string;
  /** Підпис у шапці (уже мовою людини). */
  label: string;
  isTitle: boolean;
  priority: 1 | 2 | 3;
  /** Фіксована ширина, px; `null` — гнучка. */
  width: number | null;
  className: string;
  /** Поле позначене сортованим у метаданих (є індекс, LST-10): шапка дає сортування. */
  sortable?: boolean | undefined;
}

/** Вигляд колонки за типом: клас (вирівнювання, components.md, розд. 7) і фіксована ширина. */
const TYPE_LAYOUT: Readonly<Record<string, { className: string; width: number }>> = {
  phone: { className: 'col-phone', width: 168 },
  next_activity: { className: 'col-next', width: 168 },
  stage: { className: 'col-stage', width: 120 },
  picklist: { className: 'col-stage', width: 120 },
  money: { className: 'col-num', width: 112 },
  number: { className: 'col-num', width: 112 },
  integer: { className: 'col-num', width: 112 },
  decimal: { className: 'col-num', width: 112 },
  percent: { className: 'col-num', width: 112 },
  date: { className: 'col-date', width: 112 },
  datetime: { className: 'col-date', width: 136 },
  boolean: { className: 'col-bool', width: 96 },
};

/** Клас колонки за типом поля: вирівнювання й вигляд комірки (components.md, розд. 7). */
export function columnClass(type: string, isTitle: boolean): string {
  if (isTitle) return 'title-cell';
  return TYPE_LAYOUT[type]?.className ?? 'col-text';
}

/** Фіксована ширина колонки за типом поля (layout.md, розд. 5), px; `null` — гнучка. */
export function fixedWidth(type: string, isTitle: boolean): number | null {
  if (isTitle) return null;
  return TYPE_LAYOUT[type]?.width ?? null;
}

/** Поле колонки, як його бачить таблиця: тип і підпис. */
export interface ColumnField {
  type: string;
  label: string;
  sortable?: boolean | undefined;
}

/**
 * Колонки подання з метаданих у порядку подання. `describe` повертає тип і підпис поля або
 * `undefined` — поля немає в метаданих (видалене чи приховане правами, SEC-043): колонки теж немає.
 */
export function columnSpecs(
  columns: readonly { field: string; priority?: 1 | 2 | 3 | undefined }[],
  titleField: string,
  describe: (key: string) => ColumnField | undefined,
): ColumnSpec[] {
  const specs: ColumnSpec[] = [];
  for (const c of columns) {
    const field = describe(c.field);
    if (!field) continue;
    const isTitle = c.field === titleField;
    specs.push({
      key: c.field,
      type: field.type,
      label: field.label,
      isTitle,
      priority: isTitle ? 1 : (c.priority ?? 1),
      width: fixedWidth(field.type, isTitle),
      className: columnClass(field.type, isTitle),
      ...(field.sortable ? { sortable: true } : {}),
    });
  }
  return specs;
}

/** Найменша ширина таблиці з цими колонками: вибір + фіксовані + FLEX_MIN на кожну гнучку. */
export function minTableWidth(columns: readonly ColumnSpec[]): number {
  return columns.reduce((sum, c) => sum + (c.width ?? FLEX_MIN), CHECK_W);
}

/** Які колонки видно в області шириною `available` px (правило — на початку файлу). */
export function fitColumns(columns: readonly ColumnSpec[], available: number): ColumnSpec[] {
  const visible = [...columns];
  while (minTableWidth(visible) > available) {
    let drop = -1;
    let dropPriority = 1;
    visible.forEach((c, i) => {
      if (c.priority >= dropPriority && c.priority > 1) {
        drop = i;
        dropPriority = c.priority;
      }
    });
    if (drop < 0) break;
    visible.splice(drop, 1);
  }
  return visible;
}

const keysOf = (columns: readonly ColumnSpec[]) => columns.map((c) => c.key).join('\n');

/**
 * Видимі колонки під фактичну ширину області `target`. Ширина — лише з ResizeObserver; до
 * першого виміру — оцінка `hint`. Ширину тримаємо в ref, а стан — лише набір видимих колонок:
 * зміна ширини без зміни набору рендеру не викликає (рев'ю S4). Колбек ResizeObserver приходить
 * після розкладки, але ДО малювання кадру: коли набір змінився, рендеримо синхронно (flushSync),
 * тож кадр зі старим набором на екран не потрапляє — без зсуву вмісту (CLS).
 */
export function useFittedColumns(
  columns: readonly ColumnSpec[],
  target: RefObject<HTMLElement | null>,
  mounted: boolean,
  hint: number,
): readonly ColumnSpec[] {
  const width = useRef(hint);
  const latest = useRef(columns);
  latest.current = columns;
  // Лічильник замість ширини в стані: рендер — лише коли набір колонок змінився.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  // Нові колонки подання — набір рахуємо одразу за останньою відомою шириною, без чекання resize.
  const keys = keysOf(fitColumns(columns, width.current));
  const visible = useMemo(() => {
    const shown = new Set(keys.split('\n'));
    return columns.filter((c) => shown.has(c.key));
  }, [columns, keys]);

  useLayoutEffect(() => {
    const element = target.current;
    if (!mounted || !element) return;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.round(entry?.contentBoxSize?.[0]?.inlineSize ?? 0);
      if (next <= 0) return;
      const cols = latest.current;
      const before = keysOf(fitColumns(cols, width.current));
      width.current = next;
      const after = keysOf(fitColumns(cols, next));
      if (after !== before) flushSync(rerender);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [mounted, target]);

  return visible;
}
